"use client";

import React, { useState } from 'react';
import { Check, Loader2, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/mixpanel';
import { useAppSelector } from '@/app/store/hooks';
import { Modal } from '@/components/ui/modal';

import type { ProductType } from '@/types/cart.types';

interface PlanCardProps {
    id?: string;
    title: string;
    description: string;
    image: string;
    features: string[];
    onBuy: () => void;
    isLoading?: boolean;
    previewPdf?: string;
    onPreviewClick?: () => void;
}

const PlanCard = ({ id, title, description, image, features, onBuy, isLoading, previewPdf, onPreviewClick }: PlanCardProps) => (
    <div id={id} className="bg-white rounded-[16px] flex flex-col h-full shadow-sm overflow-hidden border border-slate-100">
        {/* Image Container */}
        <div className="px-6 pt-8 pb-4">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">{title}</h2>
            <div
                className={cn(
                    "aspect-[16/10] w-full rounded-xl overflow-hidden relative group"
                )}
            >
                <img src={image} alt={title} className="w-full h-full object-cover" />
                {previewPdf && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onPreviewClick?.();
                        }}
                        className="absolute top-3 right-3 w-10 h-10 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all duration-200 border border-white/20 shadow-lg group/btn"
                        title="View Preview"
                    >
                        <Eye className="w-5 h-5 transition-transform duration-200 group-hover/btn:scale-110" />
                    </button>
                )}
            </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-8 flex flex-col flex-1">
            <p className="text-[14px] text-slate-500 font-medium leading-relaxed mb-8">
                {description}
            </p>

            <div className="mb-8">
                <h4 className="text-[14px] font-bold text-slate-900 mb-4">Features:</h4>
                <ul className="space-y-3">
                    {features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <div className="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                                <Check className="w-2.5 h-2.5 text-white" />
                            </div>
                            <span className="text-[13px] text-slate-600 font-medium">{feature}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="mt-auto">
                <button 
                    onClick={onBuy}
                    disabled={isLoading}
                    className="w-full py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-lg shadow-md shadow-blue-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70"
                >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy Now"}
                </button>
            </div>
        </div>
    </div>
);

interface DocumentPlansProps {
    onAddToCart: (productType: ProductType) => void;
}

export const DocumentPlans = ({ onAddToCart }: DocumentPlansProps) => {
    const [activePdf, setActivePdf] = useState<string | null>(null);

    const handleBuyAction = (productType: ProductType) => {
        trackEvent("Plan Buy Clicked", { plan: productType });
        onAddToCart(productType);
    };

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[840px] mx-auto">
                <PlanCard
                    id="documents"
                    title="Documents"
                    description="Research-backed Word documents for your business needs."
                    image="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775801541214_9omio1q80zi_Document.jpg"
                    previewPdf=""
                    features={[
                        "First 5 documents always free",
                        "Quick Research mode",
                        "Download in Word / DOCX format",
                        "No expiry on free credits"
                    ]}
                    onBuy={() => handleBuyAction('DOCUMENT')}
                    onPreviewClick={() => setActivePdf("")}
                />
                <PlanCard
                    id="market-research"
                    title="Market Research"
                    description="In-depth market analysis reports powered by deep research."
                    image="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775801541060_9vd5sslpej_MarketResearch.jpg"
                    previewPdf=""
                    features={[
                        "First 5 reports always free",
                        "Deep Research mode",
                        "Download in Word / DOCX format",
                        "No expiry on free credits"
                    ]}
                    onBuy={() => handleBuyAction('MARKET_RESEARCH')}
                    onPreviewClick={() => setActivePdf("")}
                />
            </div>

            <Modal isOpen={activePdf !== null} onClose={() => setActivePdf(null)} className="max-w-3xl p-0">
                <div className="aspect-[210/297] w-full">
                    {activePdf ? (
                        <iframe src={activePdf} className="w-full h-full rounded-2xl" title="PDF Preview" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
                            Preview not available yet
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};
